import { ApiClientType } from '../../../../../../api/_legacy/generated-api'
import {
  ApiMutualFundAssetRead,
  ApiSegregatedPortfolioAssetRead
} from '../../../../../../api/rtk-api'
import {
  Break,
  Fees,
  FieldSet,
  FormItem,
  Label,
  LegacyDatePicker,
  ProtectionAssetSelector,
  RadioWithLabel,
  Section,
  SliderWithTextInput,
  Space,
  ValidationMessage
} from '../../../../../../components/core-v2'
import { useAppSelector } from '../../../../store/hooks'
import { useActions } from '../../../../store/use-actions'
import { StyledCharges } from '../../../01-note/existing-note/details-summary/primitives'
import { StyledChargesBlock } from '../../primitives'
import {
  actions as rawActions,
  ProtectionState,
  StrategyActions,
  StrategyStepState
} from '../../state/strategy-slice'
import { CoreProtectionEditor } from './core-protection-editor'

export type ProtectionEditorProps = {
  actions: StrategyActions
  clientType: ApiClientType
  commonState: StrategyStepState
  isGeneratingCharts: boolean
  state: ProtectionState
}

export function ProtectionEditorDumb({
  actions,
  clientType,
  commonState,
  isGeneratingCharts,
  state
}: ProtectionEditorProps) {
  const onInspectAsset = (assetId: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead) => {
    actions.inspectAsset(assetId)
  }
  return (
    <fieldset disabled={isGeneratingCharts}>
      <Space mx={5} my={6}>
        <FormItem>
          <Label>Choose a type of protection</Label>
          <FieldSet direction="horizontal">
            {state.continuous.isAllowed && (
              <RadioWithLabel
                checked={state.type === 'continuous'}
                data-testid="option-continuous"
                onChange={() => actions.setProtectionType('continuous')}
              >
                Continuous
              </RadioWithLabel>
            )}

            {state.term.isAllowed && (
              <RadioWithLabel
                checked={state.type === 'term'}
                data-testid="option-term"
                onChange={() => actions.setProtectionType('term')}
              >
                Term
              </RadioWithLabel>
            )}
          </FieldSet>
        </FormItem>

        {state.type && (
          <>
            <CoreProtectionEditor
              actions={actions}
              clientType={clientType}
              commonState={state}
              isGeneratingCharts={isGeneratingCharts}
              state={state[state.type.toString()]}
            />

            {state.type === 'term' && (
              <Space mb={5}>
                <FormItem>
                  <Label>Trade maturity date</Label>
                  <LegacyDatePicker
                    maxDate={new Date(state.term.maturityDate.max)}
                    minDate={new Date(state.term.maturityDate.min)}
                    shouldAllowPublicHolidays={false}
                    testId="maturityDate"
                    value={new Date(state.term.maturityDate.value)}
                    onChange={date =>
                      actions.setProtectionMaturityDate(
                        `${String(date.getFullYear())}-${String(date.getMonth() + 1)}-${String(
                          date.getDate()
                        )}`
                      )
                    }
                  />
                  <ValidationMessage errors={commonState.errors} fieldName="maturityDate" />
                </FormItem>
              </Space>
            )}

            {state.type && (
              <>
                <Space mb={6}>
                  <FormItem>
                    <Label>Leverage ratio</Label>
                    <SliderWithTextInput
                      data-testid="leverage-ratio"
                      max={state[state.type.toString()].leverageRatio.max}
                      min={state[state.type.toString()].leverageRatio.min}
                      step={0.025}
                      value={state[state.type.toString()].leverageRatio.value}
                      onChange={value => actions.setProtectionLeverageRatio(value)}
                    />
                  </FormItem>
                </Space>
                <Space pb={6}>
                  <Break />
                </Space>
              </>
            )}

            {state.type === 'continuous' && (
              <ProtectionAssetSelector
                asset={state.continuous.protectionAsset as any}
                assets={commonState.protectionAssets as any}
                onInspectAsset={onInspectAsset}
                onSelectAsset={asset =>
                  actions.setProtectionContinuousProtectionAsset(asset as any)
                }
              />
            )}

            {state.type === 'term' && (
              <ProtectionAssetSelector
                asset={state.term.protectionAsset as any}
                assets={commonState.protectionAssets as any}
                onInspectAsset={onInspectAsset}
                onSelectAsset={asset => actions.setProtectionTermProtectionAsset(asset as any)}
              />
            )}
          </>
        )}

        {state.type === 'continuous' && (
          <Space my={6}>
            <StyledChargesBlock>
              <Section type="secondary">
                <StyledCharges>
                  <Fees fees={state.continuous.fees} title="Charges" />
                </StyledCharges>
              </Section>
            </StyledChargesBlock>
          </Space>
        )}

        {state.type === 'term' && (
          <Space my={6}>
            <StyledChargesBlock>
              <Section type="secondary">
                <StyledCharges>
                  <Fees fees={state.term.fees} title="Charges" />
                </StyledCharges>
              </Section>
            </StyledChargesBlock>
          </Space>
        )}
      </Space>
    </fieldset>
  )
}

export function ProtectionEditor() {
  const state = useAppSelector(state => state.strategy.selectedStrategy.protection)
  const commonState = useAppSelector(state => state.strategy)
  const clientType = useAppSelector(state => state.builder.client?.clientType)

  const actions = useActions(rawActions)

  return (
    <ProtectionEditorDumb
      actions={actions}
      clientType={clientType}
      commonState={commonState}
      isGeneratingCharts={
        commonState.isGeneratingPerformanceChart || commonState.isGeneratingProjectionChart
      }
      state={state}
    />
  )
}
